/**
 * `BarSeries` — one vertical bar per year (or per practice group), with an optional mean
 * line across the plot. UC2's card charts: cropland share by year, rainfall by year with
 * the 2017–2025 average, practice adoption against the programme mean (client §3.1).
 *
 * Hand-built SVG in the same register as the other charts here (the repo forbids chart
 * libraries): viewBox-scaled, `role="img"` with a full plain-text readout carrying every
 * bar with its own label and unit, so the chart is readable by a screen reader and
 * assertable by a test without measuring pixels.
 *
 * Pure and translation-free, like `DimensionBars`/`PairedBars`: every string — the aria
 * label, the mean's name, the unit — is passed in already localised.
 */
import { fmtNum } from '../../../lib/format';

/** Chart width, matched to the Analysis dock's own body width. */
export const BS_W = 394;
/** Plot height, above the year labels. */
export const BS_PLOT_H = 72;
/** Room under the plot for the category labels. */
export const BS_LABEL_H = 14;
export const BS_PAD_TOP = 8;
export const BS_PAD_X = 2;
export const BS_H = BS_PAD_TOP + BS_PLOT_H + BS_LABEL_H;

const BAR_COLOR = '#2f6d4f';
const MEAN_COLOR = 'var(--ink-3)';

export interface BarSeriesBar {
  /** Stable key and `data-bar` value — the year, or the practice group id. */
  id: string;
  /** What is printed under the bar: a year, or a short group name. */
  label: string;
  value: number | null;
  /** The bar the card is about (this watershed's own group) — drawn solid, others faded. */
  highlight?: boolean;
  color?: string;
}

export interface BarSeriesProps {
  bars: BarSeriesBar[];
  ariaLabel: string;
  /** Drawn as a dashed line across the plot, and named in the readout. */
  mean?: number | null;
  meanLabel?: string;
  unit?: string;
  decimals?: number;
  /** `data-testid` of the `<svg>`, so a card with three charts can address each one. */
  testId?: string;
}

/** One bar's own text — shared by the readout and the `<title>`, so they cannot disagree. */
function valueText(value: number | null, unit: string, decimals: number): string {
  return value == null ? '—' : `${fmtNum(value, decimals)}${unit ? ` ${unit}` : ''}`;
}

export function BarSeries({
  bars,
  ariaLabel,
  mean = null,
  meanLabel,
  unit = '',
  decimals = 1,
  testId = 'bar-series-chart',
}: BarSeriesProps) {
  const values = bars
    .map((b) => b.value)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (typeof mean === 'number' && Number.isFinite(mean)) values.push(mean);
  // Bars grow from a zero baseline: a cropland share or a rainfall total is a quantity, and
  // a chart that cut the axis would exaggerate every year-to-year wobble the client's
  // captions are deliberately cautious about.
  const hi = values.length ? Math.max(...values, 0) : 1;
  const span = hi || 1;
  const n = Math.max(bars.length, 1);
  const slot = (BS_W - 2 * BS_PAD_X) / n;
  const barW = Math.max(3, slot * 0.68);
  const baseY = BS_PAD_TOP + BS_PLOT_H;

  const readout =
    `${ariaLabel} — ` +
    bars.map((b) => `${b.label} ${valueText(b.value, unit, decimals)}`).join('. ') +
    (typeof mean === 'number'
      ? `. ${meanLabel ?? 'mean'} ${valueText(mean, unit, decimals)}`
      : '');
  const meanY =
    typeof mean === 'number' && Number.isFinite(mean)
      ? baseY - (mean / span) * BS_PLOT_H
      : null;

  return (
    <svg
      className="intel-chart bar-series"
      viewBox={`0 0 ${BS_W} ${BS_H}`}
      role="img"
      aria-label={readout}
      data-testid={testId}
    >
      {bars.map((bar, i) => {
        const x = BS_PAD_X + i * slot + (slot - barW) / 2;
        const h =
          bar.value == null || !Number.isFinite(bar.value)
            ? 0
            : Math.max(1, (Math.max(bar.value, 0) / span) * BS_PLOT_H);
        return (
          <g key={bar.id} className="bs-bar" data-bar={bar.id}>
            <title>{`${bar.label} · ${valueText(bar.value, unit, decimals)}`}</title>
            {h > 0 ? (
              <rect
                x={x}
                y={baseY - h}
                width={barW}
                height={h}
                rx={2}
                fill={bar.color ?? BAR_COLOR}
                opacity={bar.highlight === false ? 0.45 : 1}
              />
            ) : null}
            <text
              className="bs-label"
              x={x + barW / 2}
              y={baseY + BS_LABEL_H - 4}
              textAnchor="middle"
              fontSize="10"
            >
              {bar.label}
            </text>
          </g>
        );
      })}
      {meanY != null ? (
        <>
          <line
            data-mean=""
            x1={BS_PAD_X}
            x2={BS_W - BS_PAD_X}
            y1={meanY}
            y2={meanY}
            stroke={MEAN_COLOR}
            strokeWidth={1.5}
            strokeDasharray="3,2"
          />
          {meanLabel ? (
            <text
              className="bs-meanlabel"
              x={BS_W - BS_PAD_X}
              y={Math.max(8, meanY - 2)}
              textAnchor="end"
              fontSize="9"
              fill={MEAN_COLOR}
            >
              {meanLabel}
            </text>
          ) : null}
        </>
      ) : null}
    </svg>
  );
}

export default BarSeries;
